import { useRef } from 'react';
import { throttle } from 'lodash';
import { Button, makeStyles, TextField } from '@material-ui/core';
import { CustomizedTypography, Modal } from '@components/Customized';
import { CapaErrorCode } from '@generated/graphql';
import { processEvents } from '@utils/customEvent';
import { PaymentModalProps } from './types';
import usePaymentModal from './usePaymentModal';

const RefundRequestModal: React.FunctionComponent<
  PaymentModalProps & {
    rfqId?: string;
    quotationPartnerId?: string;
    purchaseOrderId: string;
    amount: number;
  }
> = ({
  rfqId = '',
  quotationPartnerId = '',
  purchaseOrderId,
  amount,
  openButton
}) => {
  const {
    open,
    onOpen,
    onClose,
    loading,
    setLoading,
    enqueueSnackbar
  } = usePaymentModal();
  const reason = useRef('');
  const textCounter = useRef<HTMLSpanElement>(null);
  const submitButton = useRef<HTMLButtonElement>(null);
  const classes = useStyles();

  const onSubmit = throttle(async () => {
    if (!reason.current) {
      throw new Error('환불사유를 입력해주세요.');
    }

    setLoading(true);
    try {
      const result = await processEvents.환불요청({
        rfqId,
        quotationPartnerId,
        purchaseOrderId,
        requestRefund: {
          amount: amount,
          reason: reason.current
        }
      });
      const isSuccess = result.result === CapaErrorCode.Success;
      if (!isSuccess) throw new Error(result.errorMessage);
      enqueueSnackbar('환불 요청이 파트너에게 발송되었습니다.');
      onClose();
    } catch (e) {
      enqueueSnackbar('환불 요청에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  }, 500);

  return (
    <>
      {openButton(onOpen)}
      <Modal open={open} onClose={onClose}>
        <Modal.CloseButton onClose={onClose} />
        <Modal.Title>환불 요청</Modal.Title>

        <Modal.ContentSection>
          <Modal.Text
            className={classes.text}
            variant="body1"
            color="textPrimary"
            fontWeight="500"
          >
            환불요청 사유 입력
            <span ref={textCounter}>(0/500)</span>
          </Modal.Text>
          <TextField
            onChange={({ currentTarget }) => {
              const input = currentTarget?.value ?? '';
              const trimmedInput = input.trim();
              const hasReason = trimmedInput.length > 0;

              if (!submitButton.current) return;

              if (hasReason) {
                submitButton.current.classList.remove('Mui-disabled');
                submitButton.current.disabled = false;
              } else {
                submitButton.current.classList.add('Mui-disabled');
                submitButton.current.disabled = true;
              }

              if (trimmedInput.length > 500) return;
              reason.current = trimmedInput;
              if (!textCounter.current) return;
              textCounter.current.innerText = `${trimmedInput.length}/500`;
            }}
            onBlur={({ currentTarget: { value } }) => {
              const input = value ?? '';
              const trimmedInput = input.trim();

              if (trimmedInput.length > 500) {
                reason.current = trimmedInput.slice(500);
              }
            }}
            inputProps={{ maxLength: 500 }}
            placeholder="환불 사유를 알려주세요."
            multiline
            fullWidth
            variant="outlined"
            minRows={4}
            maxRows={4}
          />
        </Modal.ContentSection>

        <Modal.ButtonSection>
          <Modal.Button color="default" onClick={onClose} disabled={loading}>
            취소
          </Modal.Button>
          <Button
            className="Mui-disabled"
            color="primary"
            variant="contained"
            ref={submitButton}
            disabled={loading}
            style={{
              borderRadius: 8
            }}
            disableElevation
            onClick={onSubmit}
          >
            <CustomizedTypography
              variant="body1"
              color="inherit"
              fontWeight="500"
            >
              완료
            </CustomizedTypography>
          </Button>
        </Modal.ButtonSection>
      </Modal>
    </>
  );
};

export default RefundRequestModal;

const useStyles = makeStyles(
  {
    text: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 12,
      '& span': {
        fontWeight: 400
      }
    }
  },
  { classNamePrefix: 'purhcase-order-reject' }
);
